import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    Notifications as NotificationsIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    HelpOutline as HelpOutlineIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader, AkceFormDialog } from '../../components/admin';
import DochazkaSekce from '../../components/admin/DochazkaSekce';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { useAuth } from '../../context/AuthContext';
import {
    useAkceDetailQuery,
    useSubmitOdpovedMutation,
    useSendAkceNotifikaceMutation,
    useDeleteAkceMutation,
} from '../../api/JsdhNotifikaceService/service/AkceMutations';

dayjs.locale('cs');

const TYP_LABELS: Record<string, string> = {
    SKOLENI: 'Školení',
    KONTROLA_TECHNIKY: 'Kontrola techniky',
    SCHUZKA: 'Schůzka',
    JINE: 'Jiné',
};

const TYP_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'default'> = {
    SKOLENI: 'primary',
    KONTROLA_TECHNIKY: 'warning',
    SCHUZKA: 'info',
    JINE: 'default',
};

const DetailAkcePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const akceId = id ? parseInt(id, 10) : null;

    const { data: detail, isLoading, error } = useAkceDetailQuery(akceId);
    const submitOdpovedMutation = useSubmitOdpovedMutation();
    const sendNotifikaceMutation = useSendAkceNotifikaceMutation();
    const deleteMutation = useDeleteAkceMutation();

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openNotifikaceDialog, setOpenNotifikaceDialog] = useState(false);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba při načítání detailu akce: {error.message}</Alert>;
    }

    if (!detail) {
        return <Alert severity="warning">Akce nenalezena.</Alert>;
    }

    const { akce, odpovedi, pocetBudu, pocetNebudu, pocetBezOdpovedi, mojeOdpoved } = detail;

    const handleOdpoved = (odpoved: 'BUDU' | 'NEBUDU') => {
        if (akceId) {
            submitOdpovedMutation.mutate({ akceId, odpoved });
        }
    };

    const handleDelete = () => {
        if (akceId) {
            deleteMutation.mutate(akceId, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    navigate('/admin/akce');
                },
            });
        }
    };

    const handleSendNotifikace = () => {
        if (akceId) {
            sendNotifikaceMutation.mutate(akceId, {
                onSuccess: () => setOpenNotifikaceDialog(false),
            });
        }
    };

    const isDeadlinePassed = akce.deadlineOdpovedi
        ? dayjs(akce.deadlineOdpovedi).isBefore(dayjs())
        : false;

    // HO-130: admin vidí i cizí akci s viditelnost=VYBRANI kvůli správě, ale server
    // by mu odpověď odmítl 403. `=== false`, protože starší backend příznak
    // neposílá a `undefined` musí znamenat „jako dosud", ne „zakázáno".
    const mimoCilovouSkupinu = detail.muzuOdpovedet === false;

    return (
        <Box>
            {/* Header */}
            <PageHeader
                title={akce.nazev}
                icon={
                    <IconButton onClick={() => navigate('/admin/akce')}>
                        <ArrowBackIcon />
                    </IconButton>
                }
                action={
                    <>
                        <Chip
                            label={TYP_LABELS[akce.typ] || akce.typ}
                            color={TYP_COLORS[akce.typ] || 'default'}
                        />
                        {isAdmin && akceId && (
                            <>
                                <Tooltip title="Odeslat notifikaci">
                                    <Button
                                        variant="outlined"
                                        startIcon={<NotificationsIcon />}
                                        onClick={() => setOpenNotifikaceDialog(true)}
                                        disabled={sendNotifikaceMutation.isPending}
                                    >
                                        Odeslat notifikaci
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Upravit akci">
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => setOpenEditDialog(true)}
                                    >
                                        Upravit
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Smazat akci">
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setOpenDeleteDialog(true)}
                                    >
                                        Smazat
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                    </>
                }
            />

            {/* Info Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 3 }}>
                {/* Detail akce */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Informace o akci</Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 180 }}>Typ</TableCell>
                                <TableCell>{TYP_LABELS[akce.typ] || akce.typ}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Datum a čas od</TableCell>
                                <TableCell>
                                    {akce.datumCasOd
                                        ? dayjs(akce.datumCasOd).format('dddd D. MMMM YYYY, HH:mm')
                                        : '–'}
                                </TableCell>
                            </TableRow>
                            {akce.datumCasDo && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Datum a čas do</TableCell>
                                    <TableCell>
                                        {dayjs(akce.datumCasDo).format('dddd D. MMMM YYYY, HH:mm')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {akce.misto && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Místo</TableCell>
                                    <TableCell>{akce.misto}</TableCell>
                                </TableRow>
                            )}
                            {akce.popis && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Popis</TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{akce.popis}</TableCell>
                                </TableRow>
                            )}
                            {akce.deadlineOdpovedi && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Deadline odpovědí</TableCell>
                                    <TableCell>
                                        {dayjs(akce.deadlineOdpovedi).format('D. M. YYYY HH:mm')}
                                        {isDeadlinePassed && (
                                            <Chip label="Vypršelo" color="error" size="small" sx={{ ml: 1 }} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Viditelnost</TableCell>
                                <TableCell>
                                    {akce.viditelnost === 'VYBRANI' ? 'Pouze vybraní členové' : 'Všichni členové'}
                                </TableCell>
                            </TableRow>
                            {akce.notifikacePredAkciMinuty && akce.notifikacePredAkciMinuty > 0 && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Připomínka</TableCell>
                                    <TableCell>{akce.notifikacePredAkciMinuty} minut před akcí</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>

                {/* Statistiky odpovědí + moje odpověď */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Statistiky */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Odpovědi</Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Card variant="outlined" sx={{ flex: 1, textAlign: 'center' }}>
                                <CardContent>
                                    <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                                    <Typography variant="h4">{pocetBudu}</Typography>
                                    <Typography variant="body2" color="text.secondary">BUDU</Typography>
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ flex: 1, textAlign: 'center' }}>
                                <CardContent>
                                    <CancelIcon color="error" sx={{ fontSize: 32 }} />
                                    <Typography variant="h4">{pocetNebudu}</Typography>
                                    <Typography variant="body2" color="text.secondary">NEBUDU</Typography>
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ flex: 1, textAlign: 'center' }}>
                                <CardContent>
                                    <HelpOutlineIcon color="disabled" sx={{ fontSize: 32 }} />
                                    <Typography variant="h4">{pocetBezOdpovedi}</Typography>
                                    <Typography variant="body2" color="text.secondary">Bez odpovědi</Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Paper>

                    {/* Moje odpověď */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Moje odpověď</Typography>
                        {mimoCilovouSkupinu ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                Tato akce je určena jen vybraným členům. Odpovídat mohou jen oni.
                            </Typography>
                        ) : (
                            <>
                                {mojeOdpoved ? (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={mojeOdpoved}
                                            color={mojeOdpoved === 'BUDU' ? 'success' : 'error'}
                                            sx={{ fontSize: '1rem', py: 2, px: 3 }}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                                        Zatím jste neodpověděl/a
                                    </Typography>
                                )}
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Button
                                        variant={mojeOdpoved === 'BUDU' ? 'contained' : 'outlined'}
                                        color="success"
                                        startIcon={<ThumbUpIcon />}
                                        onClick={() => handleOdpoved('BUDU')}
                                        disabled={submitOdpovedMutation.isPending || isDeadlinePassed}
                                    >
                                        BUDU
                                    </Button>
                                    <Button
                                        variant={mojeOdpoved === 'NEBUDU' ? 'contained' : 'outlined'}
                                        color="error"
                                        startIcon={<ThumbDownIcon />}
                                        onClick={() => handleOdpoved('NEBUDU')}
                                        disabled={submitOdpovedMutation.isPending || isDeadlinePassed}
                                    >
                                        NEBUDU
                                    </Button>
                                </Box>
                                {isDeadlinePassed && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                        Deadline pro odpovědi vypršel
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Seznam odpovědí */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Seznam odpovědí</Typography>
                {odpovedi.length === 0 ? (
                    <Typography color="text.secondary">Zatím žádné odpovědi.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Jméno</TableCell>
                                    <TableCell>Odpověď</TableCell>
                                    <TableCell>Datum a čas</TableCell>
                                    <TableCell>Poznámka</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {odpovedi.map((odp) => (
                                    <TableRow key={odp.id}>
                                        <TableCell>{odp.jmeno || `Uživatel #${odp.uzivatelId}`}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={odp.odpoved}
                                                color={odp.odpoved === 'BUDU' ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {odp.cas ? dayjs(odp.cas).format('D.M.YYYY HH:mm') : '–'}
                                        </TableCell>
                                        <TableCell>
                                            {odp.poznamka
                                                ? odp.poznamka
                                                : <Box component="span" sx={{ color: 'text.disabled' }}>–</Box>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Docházka */}
            {isAdmin && akceId && (
                <Box sx={{ mt: 3 }}>
                    <DochazkaSekce
                        akceId={akceId}
                        dochazkaUzavrenaAt={akce.dochazkaUzavrenaAt}
                        datumCasOd={akce.datumCasOd}
                        datumCasDo={akce.datumCasDo}
                    />
                </Box>
            )}

            {/* Editace akce — zustava na detailu, po ulozeni se detail sam obnovi */}
            <AkceFormDialog
                open={openEditDialog}
                akce={akce}
                onClose={() => setOpenEditDialog(false)}
            />

            {/* Potvrzeni smazani */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat akci?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Opravdu chcete smazat akci „{akce.nazev}"? Tuto operaci nelze vrátit zpět.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Potvrzeni odeslani notifikace */}
            <Dialog open={openNotifikaceDialog} onClose={() => setOpenNotifikaceDialog(false)}>
                <DialogTitle>Odeslat notifikaci?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Opravdu chcete odeslat notifikaci o akci „{akce.nazev}"
                        {' '}
                        {detail.vybraniUzivatele && detail.vybraniUzivatele.length > 0
                            ? `vybraným členům (${detail.vybraniUzivatele.length})?`
                            : 'všem členům?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNotifikaceDialog(false)}>Zrušit</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={!sendNotifikaceMutation.isPending ? <NotificationsIcon /> : undefined}
                        onClick={handleSendNotifikace}
                        disabled={sendNotifikaceMutation.isPending}
                    >
                        {sendNotifikaceMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Odeslat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DetailAkcePage;
